import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaBook,
  FaSpinner,
  FaArrowLeft,
  FaEdit,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type Task = {
  taskId: number;
  taskType: string;
  taskOrder: number;
  taskDescription: string;
  audioReference: string | null;
  correctAnswer: string | null;
  points: number;
};

type Homework = {
  homeworkId: number;
  level: string;
  chapterNumber: number;
  title: string;
  instructions: string | null;
  totalPoints: number;
  tasks: Task[];
  createdAt: string;
};

const taskTypeLabels: Record<string, string> = {
  BEISPIEL_SATZ: "Beispiel Satz (Example Sentences)",
  SATZBAU: "Satzbau (Sentence Construction)",
  ERGANZUNGEN: "Ergänzungen (Fill in the Blanks)",
  HOREN: "Hören (Listening)",
  SCHREIBEN: "Schreiben (Writing)",
};

const taskTypeColors: Record<string, string> = {
  BEISPIEL_SATZ: "bg-blue-100 text-blue-800",
  SATZBAU: "bg-green-100 text-green-800",
  ERGANZUNGEN: "bg-yellow-100 text-yellow-800",
  HOREN: "bg-purple-100 text-purple-800",
  SCHREIBEN: "bg-pink-100 text-pink-800",
};

const AdminHomeworkViewPage: React.FC = () => {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const navigate = useNavigate();

  const [homework, setHomework] = useState<Homework | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchHomework();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkId]);

  const fetchHomework = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/homework/${homeworkId}/view`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        setHomework(data);
      } else {
        setError("Failed to load homework");
      }
    } catch (err) {
      console.error("Error fetching homework:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const parseCorrectAnswers = (correctAnswer: string | null): string[] => {
    if (!correctAnswer || correctAnswer.trim() === "") {
      return [];
    }

    try {
      // Try parsing as JSON array
      if (correctAnswer.trim().startsWith("[")) {
        const parsed = JSON.parse(correctAnswer);
        return Array.isArray(parsed) ? parsed : [];
      }
    } catch (e) {
      // Not JSON
    }

    // Fallback: Split by lines
    return correctAnswer
      .split("\n")
      .map((line) => line.replace(/^\d+\.\s*/, "").trim())
      .filter((line) => line !== "");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  if (!homework) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            {error || "Homework not found"}
          </p>
          <button
            onClick={() => navigate("/admin/homework")}
            className="px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            Back to Homework List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaBook />}
        title="Preview Homework"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Homework", to: "/admin/homework" },
          { label: "Preview" },
        ]}
      />

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/admin/homework")}
          className="inline-flex items-center gap-2 px-4 py-2 mb-6 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
        >
          <FaArrowLeft />
          Back to Homework List
        </button>

        {/* Homework Info Card */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {homework.title}
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                  Level: {homework.level}
                </span>
                <span className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-800 font-bold rounded-md">
                  {homework.chapterNumber}
                </span>
                <span className="text-sm text-gray-600">
                  {homework.tasks.length} Tasks
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  Total: {homework.totalPoints} Points
                </span>
              </div>
            </div>

            {/* ✅ FIXED: Action Buttons using homeworkId from params */}
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => navigate(`/admin/homework/${homeworkId}/assign`)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
              >
                <FaUsers />
                Assign to Batches
              </button>
              <button
                onClick={() => navigate(`/admin/homework/${homeworkId}/edit`)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-cyan-600 transition-colors font-semibold"
              >
                <FaEdit />
                Edit Homework
              </button>
            </div>
          </div>

          {/* Instructions */}
          {homework.instructions && (
            <div className="mt-4 p-4 bg-white border border-blue-200 rounded-lg">
              <h4 className="text-sm font-bold text-gray-900 mb-2">
                Instructions:
              </h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {homework.instructions}
              </p>
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FaCheckCircle className="text-green-600" />
            Tasks Preview
          </h3>

          {homework.tasks
            .sort((a, b) => a.taskOrder - b.taskOrder)
            .map((task, index) => {
              const correctAnswers = parseCorrectAnswers(task.correctAnswer);
              const isMultipleAnswerTask =
                task.taskType === "HOREN" || task.taskType === "ERGANZUNGEN";

              return (
                <div
                  key={task.taskId} // ✅ ADDED THIS
                  className="bg-white rounded-xl shadow-md p-6 border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-gray-900">
                      Task {task.taskOrder}
                    </h4>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold ${
                          taskTypeColors[task.taskType] ||
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {taskTypeLabels[task.taskType] || task.taskType}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800">
                        {task.points} points
                      </span>
                    </div>
                  </div>

                  {/* Task Description */}
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h5 className="text-sm font-semibold text-gray-700 mb-2">
                      Question:
                    </h5>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">
                      {task.taskDescription}
                    </p>
                  </div>

                  {/* Audio Reference */}
                  {task.taskType === "HOREN" && task.audioReference && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h5 className="text-sm font-semibold text-gray-700 mb-2">
                        🎧 Audio Reference:
                      </h5>
                      <p className="text-sm text-gray-800">
                        {task.audioReference}
                      </p>
                    </div>
                  )}

                  {/* Correct Answers */}
                  {isMultipleAnswerTask && correctAnswers.length > 0 && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h5 className="text-sm font-semibold text-green-800 mb-3">
                        ✅ Correct Answers ({correctAnswers.length}):
                      </h5>
                      <div className="space-y-2">
                        {correctAnswers.map((answer, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                          >
                            <span className="flex-shrink-0 w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                              {idx + 1}
                            </span>
                            <span className="flex-1 text-gray-800">
                              {answer || (
                                <span className="text-gray-400 italic">
                                  (empty)
                                </span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Writing Task Note */}
                  {task.taskType === "SCHREIBEN" && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        💡 This is a writing task. Students will write a text
                        response, and it will be evaluated by AI.
                      </p>
                    </div>
                  )}

                  {/* Other Task Types Note */}
                  {task.taskType !== "HOREN" &&
                    task.taskType !== "ERGANZUNGEN" &&
                    task.taskType !== "SCHREIBEN" && (
                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-700">
                          📝 This task will be evaluated by AI based on the
                          student's response.
                        </p>
                      </div>
                    )}
                </div>
              );
            })}
        </div>

        {/* ✅ FIXED: Bottom Actions using homeworkId from params */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => navigate("/admin/homework")}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
          >
            Back to List
          </button>
          <button
            onClick={() => navigate(`/admin/homework/${homeworkId}/assign`)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
          >
            <FaUsers />
            Assign to Batches
          </button>
          <button
            onClick={() => navigate(`/admin/homework/${homeworkId}/edit`)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg hover:bg-cyan-600 transition-colors font-semibold"
          >
            <FaEdit />
            Edit Homework
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminHomeworkViewPage;
